import {
    ACTIVITIES_FIELDS,
    COMPANIES_FIELDS,
    OPPORTUNITIES_FIELDS,
    CONTACTS_FIELDS,
    AGENDA_FIELDS,
    PRODUCTS_FIELDS,
    SALESORDERS_FIELDS,
} from 'constants/Fields';
import {
    ACTIVITIES,
    COMPANIES,
    OPPORTUNITIES,
    CONTACTS,
    AGENDA,
    PRODUCTS,
    SALESORDERS,
} from 'constants/Entities';
import Context from 'managers/Context';
import { FIELD_TRANSLATIONS, SPECIAL_FIELD_MAPPINGS } from './fieldTranslations';

const fieldsByEntity = {
    [ACTIVITIES.entity]: ACTIVITIES_FIELDS,
    [COMPANIES.entity]: COMPANIES_FIELDS,
    [OPPORTUNITIES.entity]: OPPORTUNITIES_FIELDS,
    [CONTACTS.entity]: CONTACTS_FIELDS,
    [AGENDA.entity]: AGENDA_FIELDS,
    [PRODUCTS.entity]: PRODUCTS_FIELDS,
    [SALESORDERS.entity]: SALESORDERS_FIELDS,
};

export const getCrudFieldConfig = (entity, fieldId) => {
    if (!entity || !fieldsByEntity[entity.entity]) return {};
    const fields = fieldsByEntity[entity.entity];
    if (!fields[fieldId]) return {};
    const field = fields[fieldId];

    return {
        id: field.id,
        serverId: field.serverId,
        fieldConfiguration: field.b2Name,
        fieldValidation: field.b4Name,
    };
};

export const getCrudFieldId = (entity, fieldId) => {
    if (!entity || !fieldsByEntity[entity.entity]) return '';
    const fields = fieldsByEntity[entity.entity];
    if (!fields[fieldId]) return '';
    return fields[fieldId].id;
};

export const SF_CRUD_TYPE_GENERAL = '0';
export const SF_CRUD_TYPE_INSERT = '1';
export const SF_CRUD_TYPE_UPDATE = '2';

/** Cache: configMap reference -> { lowercaseKey: clientId }. Built once per configMap, avoids O(n) rebuild per field. */
const configMapByLowerKeyCache = new WeakMap();

/**
 * Retrieves smart field properties based on parent field values and entity state.
 * Dynamically adjusts field properties depending on whether the entity is being created or updated.
 *
 * @param {Object} params - The function parameters
 * @param {Object} params.field - The field object containing smart field configuration
 * @param {Object} params.field.smartField - Smart field configuration with properties and parent field reference
 * @param {Object} params.configMap - Mapping between field IDs and their actual property names
 * @param {Object} params.data - The current entity data object
 * @param {boolean} params.isNewEntity - Whether this is a new entity being created (true) or an existing one being updated (false)
 * @returns {Object} - A properties object to apply to the field (may include hidden, readOnly, mandatory, etc.)
 */
export const getSmartFieldProps = ({ field, configMap, data, isNewEntity = false }) => {
    // Validate inputs
    if (!field || !field.smartField || !configMap || !data) {
        return {};
    }

    try {
        const { smartField } = field;
        const currentCrudType = isNewEntity ? SF_CRUD_TYPE_INSERT : SF_CRUD_TYPE_UPDATE;

        if (!smartField.parentFieldId) {
            return {};
        }

        // Resolve parent field client id: direct lookup, then lowercase fallback, then
        // case-insensitive map (memoized per configMap so we only build it once per schema).
        let parentFieldClientId = configMap[smartField.parentFieldId];
        if (parentFieldClientId == null) {
            const parentIdLower = smartField.parentFieldId.toLowerCase?.();
            parentFieldClientId = configMap[parentIdLower];
            if (parentFieldClientId == null && parentIdLower !== undefined) {
                let configMapByLowerKey = configMapByLowerKeyCache.get(configMap);
                if (!configMapByLowerKey) {
                    configMapByLowerKey = Object.fromEntries(
                        Object.entries(configMap).map(([k, v]) => [k.toLowerCase(), v]),
                    );
                    configMapByLowerKeyCache.set(configMap, configMapByLowerKey);
                }
                parentFieldClientId = configMapByLowerKey[parentIdLower];
            }
        }
        if (!parentFieldClientId) {
            return {};
        }

        // Resolve parent value from current form data to apply smart field props (readOnly, visible, etc.)
        const parentValue = data[parentFieldClientId];

        // If parent value is undefined or null, return empty object
        if (parentValue === undefined || parentValue === null) {
            return {};
        }

        let finalValue = parentValue;
        // Extract actual value from array or object if needed
        if (Array.isArray(parentValue) && parentValue.length > 0) {
            finalValue = parentValue[0];
        } else if (typeof parentValue === 'object' && parentValue !== null) {
            finalValue = parentValue.value !== undefined ? parentValue.value : parentValue;
        }

        // If finalValue is undefined, null, or not a valid property key, return empty object
        if (
            finalValue === undefined ||
            finalValue === null ||
            (typeof finalValue !== 'string' && typeof finalValue !== 'number')
        ) {
            return {};
        }

        // Get properties from the smart field based on the final value
        // Find the matching property by value and crudType
        const fieldProperties = Array.isArray(smartField.properties)
            ? smartField.properties.find(
                  (prop) =>
                      String(prop.value) === String(finalValue) &&
                      (prop.crudType === SF_CRUD_TYPE_GENERAL || prop.crudType === currentCrudType),
              )
            : null;

        if (fieldProperties) {
            const { crudType, visible, value, ...props } = fieldProperties;
            // Return properties excluding internal fields
            return { hidden: !visible, ...props };
        }
    } catch (error) {
        console.error('Error in getSmartFieldProps:', error);
    }

    return {};
};

/**
 * Converts a semicolon-separated string of values into an array of objects with value and label properties.
 * Uses the server list data to find matching items and extract their descriptions.
 *
 * @param {Object} params - The function parameters
 * @param {string} params.list - The name of the server list to search in
 * @param {string} params.values - Semicolon-separated string of values to match
 * @returns {Array<Object>} - Array of objects with value and label properties, or empty array if no matches found
 */
export const getValuesFromList = ({ list, values }) => {
    if (!list || !values) return [];

    try {
        const state = Context.store.getState();
        const data = state?.serverList?.[list]?.data || [];

        return (typeof values === 'string' ? values : String(values))
            .split(';')
            .filter(Boolean)
            .map((item) => {
                const listItem = data.find((li) => li && li.value === item);
                return listItem
                    ? {
                          value: listItem.value,
                          label: listItem.descripcion || listItem.label || listItem.value,
                      }
                    : null;
            })
            .filter(Boolean);
    } catch (error) {
        console.error('Error in getValuesFromList:', error);
        return [];
    }
};

// ============================================================================
// Field Name Translation Functions (B2 <-> B4)
// ============================================================================

/**
 * Converts a B4 object format to B2 object format
 * B4: { description: "X", id: Y } -> B2: { label: "X", value: "Y" }
 *
 * @param {Object} b4Object - Object with B4 format
 * @returns {Object} - Object with B2 format
 */
const convertB4ObjectToB2 = (b4Object) => {
    if (!b4Object || typeof b4Object !== 'object') return b4Object;
    if (b4Object.id !== undefined && b4Object.description !== undefined) {
        return {
            label: b4Object.description,
            value: String(b4Object.id),
        };
    }
    return b4Object;
};

/**
 * Converts a B2 object format to B4 object format
 * B2: { label: "X", value: "Y" } -> B4: { description: "X", id: Y }
 *
 * @param {Object} b2Object - Object with B2 format
 * @returns {Object} - Object with B4 format
 */
const convertB2ObjectToB4 = (b2Object) => {
    if (!b2Object || typeof b2Object !== 'object') return b2Object;
    if (b2Object.value !== undefined && b2Object.label !== undefined) {
        return {
            description: b2Object.label,
            id: Number(b2Object.value),
        };
    }
    return b2Object;
};

/**
 * Transforms a value from B2 format to B4 format
 * Handles: strings, numbers, objects, and arrays of objects
 *
 * @param {*} value - Value to transform
 * @returns {*} - Transformed value
 */
const transformValueB2ToB4 = (value) => {
    if (value === null || value === undefined) return value;

    // Handle arrays
    if (Array.isArray(value)) {
        return value.map((item) => transformValueB2ToB4(item));
    }

    // Handle objects
    if (typeof value === 'object') {
        return convertB2ObjectToB4(value);
    }

    // Handle primitives (strings, numbers, booleans)
    return value;
};

/**
 * Transforms a value from B4 format to B2 format
 * Handles: strings, numbers, objects, and arrays of objects
 *
 * @param {*} value - Value to transform
 * @returns {*} - Transformed value
 */
const transformValueB4ToB2 = (value) => {
    if (value === null || value === undefined) return value;

    // Handle arrays
    if (Array.isArray(value)) {
        return value.map((item) => transformValueB4ToB2(item));
    }

    // Handle objects
    if (typeof value === 'object') {
        return convertB4ObjectToB2(value);
    }

    // Handle primitives (strings, numbers, booleans)
    return value;
};

/**
 * Helper: Build reverse lookup maps (B4 -> B2) with memoization for performance
 */
const buildReverseMaps = (() => {
    let cache = null;
    return () => {
        if (cache) return cache;

        const b4ToB2Map = {};

        Object.keys(FIELD_TRANSLATIONS).forEach((entityKey) => {
            b4ToB2Map[entityKey] = {};
            Object.entries(FIELD_TRANSLATIONS[entityKey]).forEach(([b2, b4]) => {
                // For special fields (multiple B2 -> one B4), store first B2 field as default
                if (!b4ToB2Map[entityKey][b4]) {
                    b4ToB2Map[entityKey][b4] = b2;
                }
            });
        });

        cache = { b4ToB2Map };
        return cache;
    };
})();

/**
 * Helper: Get special field configuration for an entity
 */
const getSpecialFieldConfig = (entity, b4FieldName) => {
    if (!entity?.trueName || !SPECIAL_FIELD_MAPPINGS[entity.trueName]) return null;
    return SPECIAL_FIELD_MAPPINGS[entity.trueName][b4FieldName] || null;
};

/**
 * Translates a single field name from B2 to B4 naming convention
 *
 * @param {Object} entity - Entity object (e.g., ACTIVITIES, COMPANIES)
 * @param {string} b2FieldName - B2 field name to translate
 * @returns {string} - B4 field name or original name if no mapping exists
 */
export const translateFieldB2ToB4 = (entity, b2FieldName) => {
    if (!entity?.trueName || !b2FieldName) return '';

    const mapping = FIELD_TRANSLATIONS[entity.trueName];
    return mapping?.[b2FieldName] || b2FieldName;
};

/**
 * Translates a single field name from B4 to B2 naming convention
 *
 * @param {Object} entity - Entity object (e.g., ACTIVITIES, COMPANIES)
 * @param {string} b4FieldName - B4 field name to translate
 * @returns {string} - B2 field name or original name if no mapping exists
 */
export const translateFieldB4ToB2 = (entity, b4FieldName) => {
    if (!entity?.trueName || !b4FieldName) return '';

    const { b4ToB2Map } = buildReverseMaps();
    const mapping = b4ToB2Map[entity.trueName];
    return mapping?.[b4FieldName] || b4FieldName;
};

/**
 * Helper: Check if a value is considered empty
 * Recursively checks arrays and objects to ensure they don't contain only empty values
 * @param {*} value - Value to check
 * @returns {boolean} - True if value is empty
 */
const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;

    // Check for empty strings (including whitespace-only strings)
    if (typeof value === 'string') {
        return value.trim() === '';
    }

    // Check arrays: empty array or array with only empty values
    if (Array.isArray(value)) {
        if (value.length === 0) return true;
        // Check if all elements are empty
        return value.every((item) => isEmptyValue(item));
    }

    // Check objects: empty object or object with only empty values
    if (typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length === 0) return true;
        // Check if all values in the object are empty
        return keys.every((key) => isEmptyValue(value[key]));
    }

    return false;
};

/**
 * Removes all empty values from an object (mutates the object for performance)
 * @param {Object} obj - Object to clean
 * @returns {Object} - Same object with empty values removed
 */
const removeEmptyValues = (obj) => {
    Object.keys(obj).forEach((key) => {
        if (isEmptyValue(obj[key])) {
            delete obj[key];
        }
    });
    return obj;
};

/**
 * Translates an entire object from B2 to B4 naming convention
 * Handles special cases like array fields (e.g., contactId array -> idcontacto, idContacto2, etc.)
 * Also transforms values from B2 format to B4 format (e.g., {label, value} -> {description, id})
 * Removes empty fields (empty strings, empty arrays, null, undefined, empty objects)
 *
 * @param {Object} entity - Entity object (e.g., ACTIVITIES, COMPANIES)
 * @param {Object} dataObject - Object with B2 field names as keys
 * @returns {Object} - New object with B4 field names as keys (empty fields removed)
 */
export const translateObjectB2ToB4 = (entity, dataObject) => {
    if (!entity?.trueName || !dataObject || typeof dataObject !== 'object') return {};

    const result = {};
    const specialFieldsProcessed = new Set();

    // Get special field mappings for this entity
    const specialMappings = SPECIAL_FIELD_MAPPINGS[entity.trueName] || {};

    Object.entries(dataObject).forEach(([key, value]) => {
        const translatedKey = translateFieldB2ToB4(entity, key);

        // Check if this is a special field (array field in B4)
        const specialConfig = specialMappings[translatedKey];

        if (specialConfig && specialConfig.b2Fields) {
            // Skip if we've already processed this special field
            if (specialFieldsProcessed.has(translatedKey)) return;

            // Collect all values from the B2 fields into an array
            const arrayValues = specialConfig.b2Fields
                .map((b2Field) => dataObject[b2Field])
                .filter((val) => val !== undefined && val !== null && val !== '');

            if (arrayValues.length > 0) {
                // Transform each value in the array from B2 to B4 format
                result[translatedKey] = arrayValues.map((val) => transformValueB2ToB4(val));
                specialFieldsProcessed.add(translatedKey);
            }
        } else {
            // Simple field mapping with value transformation
            result[translatedKey] = transformValueB2ToB4(value);
        }
    });

    // Remove all empty values from the result
    const cleanedResult = removeEmptyValues(result);

    // Move Z_ fields into extraFields object
    const finalResult = {};
    const extraFields = {};

    Object.entries(cleanedResult).forEach(([key, value]) => {
        if (key.startsWith('Z_')) {
            extraFields[key] = value;
        } else {
            finalResult[key] = value;
        }
    });

    // Only add extraFields if there are any
    if (Object.keys(extraFields).length > 0) {
        finalResult.extraFields = extraFields;
    }

    return finalResult;
};

/**
 * Translates an entire object from B4 to B2 naming convention
 * Handles special cases like array fields (e.g., contactId array -> idcontacto, idContacto2, etc.)
 * Also transforms values from B4 format to B2 format (e.g., {description, id} -> {label, value})
 * Filters out unmapped B4 fields that don't have a B2 equivalent
 *
 * @param {Object} entity - Entity object (e.g., ACTIVITIES, COMPANIES)
 * @param {Object} dataObject - Object with B4 field names as keys
 * @returns {Object} - New object with B2 field names as keys
 */
export const translateObjectB4ToB2 = (entity, dataObject) => {
    if (!entity?.trueName || !dataObject || typeof dataObject !== 'object') return {};

    const result = {};
    const { b4ToB2Map } = buildReverseMaps();
    const mapping = b4ToB2Map[entity.trueName] || {};

    // Preserve extraFields if it exists (contains Z_ fields that should be kept as-is)
    if (dataObject.extraFields && typeof dataObject.extraFields === 'object') {
        result.extraFields = dataObject.extraFields;
    }

    Object.entries(dataObject).forEach(([key, value]) => {
        // Skip extraFields as it's already handled above
        if (key === 'extraFields') return;

        // Check if this is a special field (array field in B4)
        const specialConfig = getSpecialFieldConfig(entity, key);

        if (specialConfig && specialConfig.b2Fields && Array.isArray(value)) {
            // Split B4 array into multiple B2 fields
            specialConfig.b2Fields.forEach((b2Field, index) => {
                if (index < value.length && value[index] !== undefined && value[index] !== null) {
                    // Transform each array item from B4 to B2 format
                    result[b2Field] = transformValueB4ToB2(value[index]);
                }
            });
        } else {
            // Simple field mapping with value transformation
            const translatedKey = translateFieldB4ToB2(entity, key);

            // Only include field if it was actually mapped (not just returned as-is)
            // This filters out unmapped B4 fields
            if (mapping[key] || translatedKey !== key) {
                result[translatedKey] = transformValueB4ToB2(value);
            }
        }
    });

    return result;
};
